import { COUNTRY_ABBREVIATIONS, getCountryCurrency, getCountryMoneyFormat } from '@tectonics/prs/country';
import { type Amount } from './amount';
import { type FormatDecimal, formatDollar, formatLatam } from './formatting';

export const currencySymbols = {
  BRL: 'R$',
  USD: 'US$',
  MXN: '$',
  ARS: '$',
  CLP: '$',
  EUR: '€',
  COP: 'COP',
  PEN: 'S/',
  BOB: 'Bs',
  UYU: '$U',
  CRC: '₡',
  PYG: '₲',
  PAB: 'B/.',
  GTQ: 'Q',
  DOP: 'RD$',
  ZAR: 'R',
  KES: 'KSh',
  NGN: '₦',
  INR: '₹',
  TZS: 'TZS',
  UGX: 'UGX',
  ZMW: 'ZMW',
  EGP: 'EGP',
  GHS: 'GHS',
  JMD: 'JM$',
  PHP: '₱',
  THB: '฿',
  IDR: 'Rp',
  TRY: '₺',
  MYR: 'RM',
  XOF: 'XOF',
  USDC: 'USDC',
  USDT: 'USDT',
} as const;

export type CurrencyCode = keyof typeof currencySymbols;

export type CurrencySymbol = (typeof currencySymbols)[CurrencyCode];

type GetCurrencySymbol = (currencyCode: CurrencyCode) => string;

export const currencySymbolMap = new Map<CurrencyCode, CurrencySymbol>(
  Object.entries(currencySymbols) as [CurrencyCode, CurrencySymbol][]
);

const CURRENCY_OVERRIDE_MAP = new Map<CurrencyCode, CurrencyCode>([
  ['PAB', 'USD'],
]);

export function overrideCurrency(amount: Amount): Amount {
  const [currencyCode, decimal] = amount;

  const overriddenCurrency = CURRENCY_OVERRIDE_MAP.get(currencyCode);

  return overriddenCurrency ? [overriddenCurrency, decimal] : amount;
}

export const getCurrencySymbol: GetCurrencySymbol = (currencyCode: CurrencyCode) => {
  const symbol = currencySymbolMap.get(currencyCode);

  if (symbol == null) throw new Error(`Currency symbol not supported: ${currencyCode}`);

  return symbol;
};

const currenciesWithDollarFormat: ReadonlySet<CurrencyCode> = new Set<CurrencyCode>([
  'USD',
  ...COUNTRY_ABBREVIATIONS.filter((country) => getCountryMoneyFormat(country) === 'dollar').map(getCountryCurrency),
]);

export const getMoneyFormatFromCurrency = (currencyCode: CurrencyCode): FormatDecimal =>
  currenciesWithDollarFormat.has(currencyCode) ? formatDollar : formatLatam;
